import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { uploadFilesSequentially } from "@/lib/uploadFiles"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  FolderOpen,
  Loader2,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { MonthlyUploadAlert } from "@/components/MonthlyUploadAlert"
import {
  DataCard,
  DataCardActions,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import { MobileFilterSheet } from "@/components/ui/mobile-filter-sheet"
import { MobileFilterSortBar } from "@/components/ui/mobile-filter-sort-bar"
import { MobileSortSheet, type SortOption } from "@/components/ui/mobile-sort-sheet"
import { useMobileFilterSort } from "@/hooks/use-mobile-filter-sort"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { parseNiqFragranceForPreview, type NiqPreviewResult } from "@/lib/niqFragrancePreviewParser"
import { inferNiqFragranceTags } from "@/lib/niqFragranceTagInference"
import { consumer } from "@/lib/actioncable"

const MONTHS_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"]

const SORT_OPTIONS: SortOption[] = [
  { sort: "created_at", direction: "desc", label: "Tanggal terbaru" },
  { sort: "created_at", direction: "asc", label: "Tanggal terlama" },
  { sort: "gender", direction: "asc", label: "Gender A–Z" },
  { sort: "gender", direction: "desc", label: "Gender Z–A" },
  { sort: "dimension_type", direction: "asc", label: "Tipe Dimensi A–Z" },
  { sort: "dimension_type", direction: "desc", label: "Tipe Dimensi Z–A" },
  { sort: "dimension_value", direction: "asc", label: "Nilai Dimensi A–Z" },
  { sort: "dimension_value", direction: "desc", label: "Nilai Dimensi Z–A" },
  { sort: "row_count", direction: "desc", label: "Baris terbanyak" },
  { sort: "row_count", direction: "asc", label: "Baris paling sedikit" },
  { sort: "status", direction: "asc", label: "Status A–Z" },
  { sort: "status", direction: "desc", label: "Status Z–A" },
]

const STATUS_OPTIONS = ["pending", "processing", "completed", "failed", "cancelled"] as const

type UploadStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"

type UploadRow = {
  id: number
  filename: string
  gender: string
  dimension_type: string
  dimension_value: string
  status: UploadStatus
  row_count: number | null
  error_message: string | null
  imported_at: string | null
  created_at: string
  uploaded_by: string | null
}

type FileTags = { gender: string; dimension_type: string; dimension_value: string }

type TrackedUpload = {
  id: number
  filename: string
  gender: string
  dimension_type: string
  dimension_value: string
  status: UploadStatus
  row_count: number | null
  error_message: string | null
  progress_rows: number
}

type StatusUpdate = {
  type: "status_update"
  upload_id: number
  status: UploadStatus
  row_count: number | null
  error_message: string | null
}

type ProgressUpdate = {
  type: "progress_update"
  upload_id: number
  progress_rows: number
}

type Filters = {
  gender: string | null
  dimension_type: string | null
  dimension_value: string | null
  status: string | null
  search: string | null
}

interface NiqFragrancesProps {
  uploads: UploadRow[]
  total: number
  page: number
  per_page: number
  sort: string
  direction: "asc" | "desc"
  filters: Filters
  gender_options: string[]
  dimension_type_options: string[]
  available_dimension_values: string[]
  known_dimension_types: Record<string, string>
}

export default function NiqFragrances({
  uploads,
  total,
  page,
  per_page,
  sort,
  direction,
  filters,
  gender_options,
  dimension_type_options,
  available_dimension_values,
  known_dimension_types,
}: NiqFragrancesProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const folderInputRef = React.useRef<HTMLInputElement>(null)
  const activeXhrRef = React.useRef<XMLHttpRequest | null>(null)
  const importAbortedRef = React.useRef(false)
  const cancelledRef = React.useRef(false)

  const [previews, setPreviews] = React.useState<NiqPreviewResult[] | null>(null)
  const [workerProgress, setWorkerProgress] = React.useState<{ total: number; done: number } | null>(null)
  const [importFiles, setImportFiles] = React.useState<File[]>([])
  const [importing, setImporting] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null)
  const [uploadFileInfo, setUploadFileInfo] = React.useState<{ index: number; total: number; name: string } | null>(null)
  const [dragOver, setDragOver] = React.useState(false)
  const [checkedFiles, setCheckedFiles] = React.useState<Set<string>>(new Set())
  const [fileTags, setFileTags] = React.useState<Record<string, FileTags>>({})
  const [trackedUploads, setTrackedUploads] = React.useState<TrackedUpload[]>([])
  const [searchValue, setSearchValue] = React.useState(filters.search ?? "")
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())

  // Sync search input when URL changes (browser back/forward).
  React.useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  // Subscribe to ActionCable for the uploads in the live progress view.
  React.useEffect(() => {
    if (trackedUploads.length === 0) return
    const subs = trackedUploads.map((u) =>
      consumer.subscriptions.create(
        { channel: "NiqFragranceUploadChannel", upload_id: u.id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            setTrackedUploads((prev) =>
              prev.map((t) => {
                if (t.id !== data.upload_id) return t
                if (data.type === "progress_update") {
                  return { ...t, progress_rows: data.progress_rows }
                }
                return {
                  ...t,
                  status: data.status,
                  row_count: data.row_count ?? t.row_count,
                  error_message: data.error_message ?? t.error_message,
                }
              })
            )
          },
        }
      )
    )
    return () => subs.forEach((s) => s.unsubscribe())
  }, [trackedUploads.map((u) => u.id).join(",")])

  // Live-update history rows for in-flight uploads visible in the table.
  const [liveUploads, setLiveUploads] = React.useState<UploadRow[]>(uploads)
  React.useEffect(() => { setLiveUploads(uploads) }, [uploads])

  const liveInFlightKey = React.useMemo(
    () =>
      liveUploads
        .filter((u) => u.status === "pending" || u.status === "processing")
        .map((u) => u.id)
        .sort((a, b) => a - b)
        .join(","),
    [liveUploads]
  )

  React.useEffect(() => {
    if (!liveInFlightKey) return
    const ids = liveInFlightKey.split(",").map((s) => parseInt(s, 10))
    const subs = ids.map((id) =>
      consumer.subscriptions.create(
        { channel: "NiqFragranceUploadChannel", upload_id: id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            if (data.type !== "status_update") return
            setLiveUploads((prev) =>
              prev.map((u) =>
                u.id === data.upload_id
                  ? {
                      ...u,
                      status: data.status,
                      row_count: data.row_count ?? u.row_count,
                      error_message: data.error_message ?? u.error_message,
                    }
                  : u
              )
            )
          },
        }
      )
    )
    return () => subs.forEach((s) => s.unsubscribe())
  }, [liveInFlightKey])

  const isInProgressView = trackedUploads.length > 0
  const isIdleView = !isInProgressView && !previews && !workerProgress

  const TERMINAL: UploadStatus[] = ["completed", "failed", "cancelled"]
  const allDone = isInProgressView && trackedUploads.every((u) => TERMINAL.includes(u.status))
  const successCount = trackedUploads.filter((u) => u.status === "completed").length
  const cancelCount = trackedUploads.filter((u) => u.status === "cancelled").length
  const failCount = trackedUploads.filter((u) => u.status === "failed").length

  const trackedIds = React.useMemo(() => new Set(trackedUploads.map((u) => u.id)), [trackedUploads])
  const visibleUploads = React.useMemo(
    () => liveUploads.filter((u) => !trackedIds.has(u.id)),
    [liveUploads, trackedIds]
  )

  const hasActiveFilter =
    !!filters.gender || !!filters.dimension_type || !!filters.dimension_value ||
    !!filters.status || !!filters.search
  const totalPages = Math.ceil(total / per_page)

  // Every checked file must have all three tags before import is allowed.
  const readyToImport = React.useMemo(() => {
    const checked = (previews ?? []).filter((p) => checkedFiles.has(p.filename))
    if (checked.length === 0) return false
    return checked.every((p) => {
      const t = fileTags[p.filename]
      return t && t.gender && t.dimension_type && t.dimension_value.trim()
    })
  }, [previews, checkedFiles, fileTags])

  // Mobile filter / sort sheets.
  const { filterOpen, setFilterOpen, sortOpen, setSortOpen, activeFilterCount, applyFilters, resetFilters } =
    useMobileFilterSort(filters, navigate,
      ["gender", "dimension_type", "dimension_value", "status", "search"] as const)
  const sortLabel =
    SORT_OPTIONS.find((o) => o.sort === sort && o.direction === direction)?.label ?? "Urutkan"

  // -------------------------------------------------------------------------
  // Navigation helper (URL-based filter/sort/page state)
  // -------------------------------------------------------------------------

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.gender) params.gender = filters.gender
    if (filters.dimension_type) params.dimension_type = filters.dimension_type
    if (filters.dimension_value) params.dimension_value = filters.dimension_value
    if (filters.status) params.status = filters.status
    if (filters.search) params.search = filters.search
    if (sort !== "created_at") params.sort = sort
    if (direction !== "desc") params.direction = direction
    if (page > 1) params.page = page
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
      else delete params[k]
    })
    setSelectedIds(new Set())
    router.get("/admin/niq-fragrances", params as Record<string, string>, {
      preserveScroll: false,
    })
  }

  function handleSortColumn(col: string) {
    if (sort === col) {
      navigate({ direction: direction === "asc" ? "desc" : "asc", page: null })
    } else {
      navigate({ sort: col, direction: "asc", page: null })
    }
  }

  function filterData(): Record<string, string | number | (string | number)[]> {
    const data: Record<string, string | number | (string | number)[]> = {}
    if (filters.gender) data.gender = filters.gender
    if (filters.dimension_type) data.dimension_type = filters.dimension_type
    if (filters.dimension_value) data.dimension_value = filters.dimension_value
    if (filters.status) data.status = filters.status
    if (filters.search) data.search = filters.search
    if (sort !== "created_at") data.sort = sort
    if (direction !== "desc") data.direction = direction
    if (page > 1) data.page = page
    return data
  }

  function handleDelete(upload: UploadRow) {
    if (!window.confirm(`Hapus upload "${upload.filename}"?\n\nSemua record yang diimport dari file ini akan ikut terhapus.`)) return
    router.delete(`/admin/niq-fragrances/${upload.id}`, {
      preserveScroll: true,
      data: filterData(),
    })
  }

  // -------------------------------------------------------------------------
  // Bulk selection / delete
  // -------------------------------------------------------------------------

  const deletableUploads = React.useMemo(
    () => visibleUploads.filter((u) => u.status !== "pending" && u.status !== "processing"),
    [visibleUploads]
  )

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === deletableUploads.length && deletableUploads.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(deletableUploads.map((u) => u.id)))
    }
  }

  function handleBulkDelete() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    if (
      !window.confirm(
        `Hapus ${ids.length} upload terpilih?\n\nSemua record yang diimport dari file-file ini juga akan terhapus.`,
      )
    ) {
      return
    }
    const data = filterData()
    data.ids = ids
    router.delete("/admin/niq-fragrances/bulk_destroy", {
      data,
      onFinish: () => setSelectedIds(new Set()),
    })
  }

  // -------------------------------------------------------------------------
  // File selection + preview
  // -------------------------------------------------------------------------

  function handleFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return
    const acceptable = Array.from(files).filter(
      (f) => f.name.endsWith(".xlsx") || f.name.endsWith(".xls")
    )
    if (acceptable.length === 0) return
    startWorkerPreview(acceptable)
  }

  async function startWorkerPreview(files: File[]) {
    cancelledRef.current = false
    setImportFiles(files)
    setPreviews(null)
    setWorkerProgress({ total: files.length, done: 0 })

    const collected: NiqPreviewResult[] = []
    for (const file of files) {
      if (cancelledRef.current) return
      const result = await parseNiqFragranceForPreview(file)
      collected.push(result)
      if (cancelledRef.current) return
      setWorkerProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : null))
    }

    if (cancelledRef.current) return
    setWorkerProgress(null)
    setPreviews(collected)
    // Check every file by default and seed empty tags for each.
    setCheckedFiles(new Set(collected.map((p) => p.filename)))
    setFileTags((prev) => {
      const next = { ...prev }
      for (const p of collected) {
        if (!next[p.filename]) {
          // Pre-fill from the filename; the admin can still edit any field.
          next[p.filename] = inferNiqFragranceTags(
            p.filename,
            gender_options,
            dimension_type_options,
            known_dimension_types,
          )
        }
      }
      return next
    })
  }

  function handleCancelPreviewInProgress() {
    cancelledRef.current = true
    setWorkerProgress(null)
    setImportFiles([])
    resetInputs()
  }

  function handleAbortImport() {
    importAbortedRef.current = true
    activeXhrRef.current?.abort()
    activeXhrRef.current = null
    setImporting(false)
    setUploadProgress(null)
  }

  function handleCancelPreview() {
    setPreviews(null)
    setImportFiles([])
    setCheckedFiles(new Set())
    resetInputs()
  }

  function toggleFile(filename: string) {
    setCheckedFiles((prev) => {
      const next = new Set(prev)
      if (next.has(filename)) next.delete(filename)
      else next.add(filename)
      return next
    })
  }

  function setTag(filename: string, patch: Partial<FileTags>) {
    setFileTags((prev) => ({
      ...prev,
      [filename]: { ...(prev[filename] ?? { gender: "", dimension_type: "", dimension_value: "" }), ...patch },
    }))
  }

  async function handleConfirmImport() {
    const filesToImport = importFiles.filter((f) => checkedFiles.has(f.name))
    if (!filesToImport.length || !readyToImport) return
    setImporting(true)
    setUploadProgress(0)
    importAbortedRef.current = false

    try {
      const { uploaded, errors, aborted } = await uploadFilesSequentially<{ queued: number; upload_ids: number[] }>({
        url: "/admin/niq-fragrances",
        files: filesToImport,
        abortRef: importAbortedRef,
        xhrRef: activeXhrRef,
        onProgress: setUploadProgress,
        onFile: ({ index, total, file }) => setUploadFileInfo({ index, total, name: file.name }),
        fields: (file) => {
          const t = fileTags[file.name]
          return {
            gender: t?.gender ?? "",
            dimension_type: t?.dimension_type ?? "",
            dimension_value: t?.dimension_value ?? "",
          }
        },
      })
      if (aborted) return

      const initial: TrackedUpload[] = uploaded.flatMap(({ file, data }) => {
        const id = data.upload_ids[0]
        if (id == null) return []
        const t = fileTags[file.name]
        return [{
          id,
          filename: file.name,
          gender: t?.gender ?? "",
          dimension_type: t?.dimension_type ?? "",
          dimension_value: t?.dimension_value ?? "",
          status: "pending" as UploadStatus,
          row_count: null,
          error_message: null,
          progress_rows: 0,
        }]
      })

      if (errors.length) alert(`Sebagian file gagal diunggah:\n${errors.join("\n")}`)

      setPreviews(null)
      setImportFiles([])
      setCheckedFiles(new Set())
      setUploadProgress(null)
      setTrackedUploads(initial)
      resetInputs()
      router.reload({ only: ["uploads", "total", "available_dimension_values"] })
    } finally {
      setImporting(false)
      setUploadProgress(null)
      setUploadFileInfo(null)
    }
  }

  async function handleCancelUpload(id: number) {
    await fetch(`/admin/niq-fragrances/${id}/cancel`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": getCsrfToken() },
    })
  }

  function handleUploadAgain() {
    setTrackedUploads([])
    router.reload({ only: ["uploads", "total", "available_dimension_values"] })
    resetInputs()
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    handleFilesSelected(e.dataTransfer.files)
  }

  function resetInputs() {
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (folderInputRef.current) folderInputRef.current.value = ""
  }

  return (
    <>
      <Head title="NIQ Fragrances">
        <meta
          name="description"
          content="Upload dan import data timeseries NielsenIQ Fragrances (Summary / Area / Channel) ke dalam penyimpanan long-format yang ternormalisasi."
        />
        <meta property="og:title" content="NIQ Fragrances" />
        <meta
          property="og:description"
          content="Upload dan import data timeseries NielsenIQ Fragrances (Summary / Area / Channel) ke dalam penyimpanan long-format yang ternormalisasi."
        />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Data</span>
          <span>/</span>
          <span className="text-ink-body font-medium">NIQ Fragrances</span>
        </div>

        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>NIQ Fragrances</h1>
              <p className="mt-1 text-sm text-ink-muted">
                Upload file Excel NielsenIQ Fragrances, tandai gender &amp; dimensi tiap file, lalu import ke timeseries.
              </p>
            </div>
            {isIdleView && (
              <>
                <div className="flex gap-2">
                  <Button variant="primary" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload File
                  </Button>
                  <Button variant="secondary" onClick={() => folderInputRef.current?.click()} disabled={importing}>
                    <FolderOpen className="mr-2 h-4 w-4" />
                    Pilih Folder
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                  {...{ webkitdirectory: "", mozdirectory: "" } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              </>
            )}
          </div>
        </div>

        <MonthlyUploadAlert categoryKey="niq_fragrances" label="NIQ Fragrances" />

        {/* Drop zone */}
        {isIdleView && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={[
              "mt-6 flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors",
              dragOver ? "border-accent bg-surface" : "border-hairline",
            ].join(" ")}
          >
            <FileSpreadsheet className="mb-3 h-10 w-10 text-ink-muted" />
            <p className="text-sm font-medium text-ink-display">
              Drag &amp; drop file .xlsx NIQ ke sini, atau pilih file/folder
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              Tiap file ditandai gender &amp; dimensi (Summary / Area / Channel) sebelum import.
            </p>
          </div>
        )}

        {/* Worker preview progress */}
        {workerProgress && (
          <div className="mt-8 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-ink-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Membaca file… {workerProgress.done}/{workerProgress.total}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCancelPreviewInProgress}>
                <Ban className="mr-1 h-3 w-3" />
                Batal
              </Button>
            </div>
            <ProgressBar value={Math.round((workerProgress.done / workerProgress.total) * 100)} />
          </div>
        )}

        {/* Preview panel */}
        {previews && (
          <div className="mt-6">
            <h2 className="mb-1 text-lg font-bold text-ink-display">Preview Import</h2>
            <p className="mb-4 text-sm text-ink-muted">
              Tandai gender, tipe dimensi, dan nilai dimensi tiap file sebelum import.
            </p>
            <div className="space-y-3">
              {previews.map((p) => (
                <PreviewCard
                  key={p.filename}
                  preview={p}
                  checked={checkedFiles.has(p.filename)}
                  tags={fileTags[p.filename] ?? { gender: "", dimension_type: "", dimension_value: "" }}
                  genderOptions={gender_options}
                  dimensionTypeOptions={dimension_type_options}
                  onToggle={() => toggleFile(p.filename)}
                  onTag={(patch) => setTag(p.filename, patch)}
                />
              ))}
            </div>

            {importing && uploadProgress !== null && (
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-ink-muted">
                      {uploadFileInfo
                        ? `Mengirim file ${uploadFileInfo.index}/${uploadFileInfo.total}…`
                        : "Mengirim file…"}{" "}
                      {uploadProgress}%
                    </p>
                    {uploadFileInfo && (
                      <p className="truncate text-xs text-ink-muted" title={uploadFileInfo.name}>
                        {uploadFileInfo.name}
                      </p>
                    )}
                  </div>
                  {uploadProgress < 100 && (
                    <Button variant="ghost" size="sm" onClick={handleAbortImport}>
                      <Ban className="mr-1 h-3 w-3" />
                      Batal
                    </Button>
                  )}
                </div>
                <ProgressBar value={uploadProgress} />
              </div>
            )}

            <div className="mt-5 flex gap-3">
              <Button variant="primary" onClick={handleConfirmImport} disabled={importing || !readyToImport}>
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {uploadProgress !== null && uploadProgress < 100 ? `Mengirim… ${uploadProgress}%` : "Mengantri…"}
                  </>
                ) : (
                  "Konfirmasi Import"
                )}
              </Button>
              <Button variant="secondary" onClick={handleCancelPreview} disabled={importing}>
                Batal
              </Button>
            </div>
          </div>
        )}

        {/* Live progress view */}
        {isInProgressView && (
          <div className="mt-6">
            <h2 className="mb-1 text-lg font-bold text-ink-display">Progress Import</h2>
            <p className="mb-4 text-sm text-ink-muted">Status tiap file diperbarui secara real-time.</p>

            <div className="space-y-3">
              {trackedUploads.map((u) => (
                <ProgressCard key={u.id} upload={u} onCancel={() => handleCancelUpload(u.id)} />
              ))}
            </div>

            {allDone && (
              <div className="mt-5 rounded-md border border-hairline bg-surface p-4">
                <p className="text-sm font-medium text-ink-display">
                  {successCount > 0 && (
                    <span className="mr-3 text-green-600 dark:text-green-400">{successCount} berhasil</span>
                  )}
                  {cancelCount > 0 && <span className="mr-3 text-ink-muted">{cancelCount} dibatalkan</span>}
                  {failCount > 0 && <span className="text-red-600 dark:text-red-400">{failCount} gagal</span>}
                </p>
                <Button variant="secondary" className="mt-3" onClick={handleUploadAgain}>
                  Upload lagi
                </Button>
              </div>
            )}
          </div>
        )}

        {/* History table */}
        <div className="mt-10">
          <h2 className="mb-4 text-lg font-bold text-ink-display">Riwayat Upload</h2>

          {/* Filter + Sort bar (mobile) */}
          <div className="mb-4 md:hidden">
            <MobileFilterSortBar
              filterCount={activeFilterCount}
              sortLabel={sortLabel}
              onFilterClick={() => setFilterOpen(true)}
              onSortClick={() => setSortOpen(true)}
            />
          </div>

          {/* Filter bar (desktop) */}
          <div className="mb-4 hidden flex-wrap items-end gap-3 md:flex">
            <Select
              value={filters.gender ?? ""}
              onChange={(e) => navigate({ gender: e.target.value || null, page: null })}
              className="w-32"
            >
              <option value="">Semua Gender</option>
              {gender_options.map((g) => (
                <option key={g} value={g} className="capitalize">{g}</option>
              ))}
            </Select>

            <Select
              value={filters.dimension_type ?? ""}
              onChange={(e) => navigate({ dimension_type: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Tipe</option>
              {dimension_type_options.map((t) => (
                <option key={t} value={t} className="capitalize">{t}</option>
              ))}
            </Select>

            <Select
              value={filters.dimension_value ?? ""}
              onChange={(e) => navigate({ dimension_value: e.target.value || null, page: null })}
              className="w-44"
            >
              <option value="">Semua Nilai Dimensi</option>
              {available_dimension_values.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </Select>

            <Select
              value={filters.status ?? ""}
              onChange={(e) => navigate({ status: e.target.value || null, page: null })}
              className="w-36"
            >
              <option value="">Semua Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                navigate({ search: searchValue || null, page: null })
              }}
            >
              <Input
                type="search"
                placeholder="Cari filename…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={() => navigate({ search: searchValue || null, page: null })}
                className="w-52"
              />
            </form>

            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({
                    gender: null, dimension_type: null, dimension_value: null,
                    status: null, search: null, page: null,
                  })
                }
              >
                Reset filter
              </Button>
            )}
          </div>

          {visibleUploads.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasActiveFilter
                ? "Tidak ada upload yang cocok dengan filter."
                : "Belum ada upload."}
            </p>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-hairline bg-surface px-3 py-2">
                  <span className="text-xs text-ink-muted">{selectedIds.size} terpilih</span>
                  <Button variant="ghost" size="sm" type="button" onClick={handleBulkDelete}>
                    <Trash2 className="mr-1 h-3 w-3" />
                    Hapus terpilih
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="ml-auto"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Batal pilih
                  </Button>
                </div>
              )}

              {/* Mobile card list */}
              <div className="space-y-3 md:hidden">
                {visibleUploads.map((u) => (
                  <UploadCard
                    key={u.id}
                    upload={u}
                    selected={selectedIds.has(u.id)}
                    onToggleSelect={() => toggleSelect(u.id)}
                    onDelete={() => handleDelete(u)}
                  />
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-md border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface">
                    <tr>
                      <th className="w-10 px-3 py-2.5">
                        <Checkbox
                          checked={
                            deletableUploads.length > 0 &&
                            selectedIds.size === deletableUploads.length
                          }
                          onChange={toggleSelectAll}
                          aria-label="Pilih semua"
                          disabled={deletableUploads.length === 0}
                        />
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">File</th>
                      <SortableHeader
                        col="gender" label="Gender"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="dimension_type" label="Tipe"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="dimension_value" label="Nilai Dimensi"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="row_count" label="Baris" align="right"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Diunggah Oleh</th>
                      <SortableHeader
                        col="status" label="Status"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <SortableHeader
                        col="created_at" label="Waktu"
                        sort={sort} direction={direction} onSort={handleSortColumn}
                      />
                      <th className="w-10 px-2 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {visibleUploads.map((u) => (
                      <UploadTableRow
                        key={u.id}
                        upload={u}
                        selected={selectedIds.has(u.id)}
                        onToggleSelect={() => toggleSelect(u.id)}
                        onDelete={() => handleDelete(u)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Summary + pagination */}
          {total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} dari{" "}
                {total} upload
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => navigate({ page: page - 1 })}
                  >
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">
                    Hal. {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => navigate({ page: page + 1 })}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile filter sheet */}
        <MobileFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          initial={{
            gender: filters.gender ?? "",
            dimension_type: filters.dimension_type ?? "",
            dimension_value: filters.dimension_value ?? "",
            status: filters.status ?? "",
            search: filters.search ?? "",
          }}
          onApply={applyFilters}
          onReset={resetFilters}
        >
          {(draft, setDraft) => (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Gender</span>
                <Select
                  value={draft.gender}
                  onChange={(e) => setDraft({ ...draft, gender: e.target.value })}
                >
                  <option value="">Semua Gender</option>
                  {gender_options.map((g) => (
                    <option key={g} value={g} className="capitalize">{g}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Tipe Dimensi</span>
                <Select
                  value={draft.dimension_type}
                  onChange={(e) => setDraft({ ...draft, dimension_type: e.target.value })}
                >
                  <option value="">Semua Tipe</option>
                  {dimension_type_options.map((t) => (
                    <option key={t} value={t} className="capitalize">{t}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Nilai Dimensi</span>
                <Select
                  value={draft.dimension_value}
                  onChange={(e) => setDraft({ ...draft, dimension_value: e.target.value })}
                >
                  <option value="">Semua Nilai Dimensi</option>
                  {available_dimension_values.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Status</span>
                <Select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  <option value="">Semua Status</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink-display">Cari filename</span>
                <Input
                  type="search"
                  value={draft.search}
                  onChange={(e) => setDraft({ ...draft, search: e.target.value })}
                />
              </label>
            </>
          )}
        </MobileFilterSheet>

        {/* Mobile sort sheet */}
        <MobileSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          current={{ sort, direction }}
          options={SORT_OPTIONS}
          onSelect={(opt) => {
            navigate({ sort: opt.sort, direction: opt.direction })
            setSortOpen(false)
          }}
        />
      </AppShell>
    </>
  )
}

function SortableHeader({
  col,
  label,
  sort,
  direction,
  onSort,
  align = "left",
}: {
  col: string
  label: string
  sort: string
  direction: "asc" | "desc"
  onSort: (col: string) => void
  align?: "left" | "right"
}) {
  const active = sort === col
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2.5 font-medium text-ink-muted hover:text-ink-body text-${align}`}
      onClick={() => onSort(col)}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        {active ? (
          direction === "asc" ? (
            <ArrowUp className="h-3 w-3 text-accent" />
          ) : (
            <ArrowDown className="h-3 w-3 text-accent" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  )
}

function PreviewCard({
  preview,
  checked,
  tags,
  genderOptions,
  dimensionTypeOptions,
  onToggle,
  onTag,
}: {
  preview: NiqPreviewResult
  checked: boolean
  tags: FileTags
  genderOptions: string[]
  dimensionTypeOptions: string[]
  onToggle: () => void
  onTag: (patch: Partial<FileTags>) => void
}) {
  const isUnknown = "unknown" in preview && preview.unknown
  const periodSummary =
    !isUnknown && preview.periods.length > 0
      ? `${formatPeriod(preview.periods[0])} – ${formatPeriod(preview.periods[preview.periods.length - 1])} (${preview.periods.length} bulan)`
      : null

  return (
    <div
      className={[
        "rounded-md border p-4",
        "border-hairline bg-surface",
        !checked ? "opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Checkbox
            id={`check-${preview.filename}`}
            checked={checked}
            onChange={onToggle}
            className="mt-0.5 shrink-0"
          />
          <label
            htmlFor={`check-${preview.filename}`}
            className="min-w-0 cursor-pointer truncate font-normal text-ink-body"
            title={preview.filename}
          >
            {preview.filename}
          </label>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-4 pl-6 text-xs text-ink-muted">
        {isUnknown ? (
          <span className="text-amber-600 dark:text-amber-400">{preview.reason}</span>
        ) : (
          <>
            <span>Periode: <strong className="text-ink-body">{periodSummary ?? "—"}</strong></span>
            <span>Estimasi baris: <strong className="text-ink-body">{preview.rowCount.toLocaleString()}</strong></span>
          </>
        )}
      </div>

      {/* Per-file tags */}
      <div className="mt-3 grid grid-cols-1 gap-3 pl-6 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink-display">Gender</span>
          <Select value={tags.gender} onChange={(e) => onTag({ gender: e.target.value })} disabled={!checked}>
            <option value="">Pilih gender…</option>
            {genderOptions.map((g) => (
              <option key={g} value={g} className="capitalize">{g}</option>
            ))}
          </Select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink-display">Tipe Dimensi</span>
          <Select
            value={tags.dimension_type}
            onChange={(e) => onTag({ dimension_type: e.target.value })}
            disabled={!checked}
          >
            <option value="">Pilih tipe…</option>
            {dimensionTypeOptions.map((d) => (
              <option key={d} value={d} className="capitalize">{d}</option>
            ))}
          </Select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink-display">Nilai Dimensi</span>
          <Input
            type="text"
            placeholder="cth. National, Sumatera, Minimarket"
            value={tags.dimension_value}
            onChange={(e) => onTag({ dimension_value: e.target.value })}
            disabled={!checked}
          />
        </label>
      </div>
    </div>
  )
}

function ProgressCard({ upload, onCancel }: { upload: TrackedUpload; onCancel: () => void }) {
  const isInFlight = upload.status === "pending" || upload.status === "processing"
  const showProgress = upload.status === "processing" && upload.progress_rows > 0

  return (
    <div className="rounded-md border border-hairline bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink-display" title={upload.filename}>
            {upload.filename}
          </p>
          <p className="mt-0.5 text-xs capitalize text-ink-muted">
            {upload.gender}
            {upload.dimension_type ? " · " : ""}
            {upload.dimension_type}
            {upload.dimension_value ? ` · ${upload.dimension_value}` : ""}
          </p>
          {upload.status === "completed" && upload.row_count != null && (
            <p className="mt-0.5 text-xs text-ink-muted">{upload.row_count.toLocaleString()} record diimport</p>
          )}
          {showProgress && (
            <p className="mt-0.5 text-xs text-ink-muted">{upload.progress_rows.toLocaleString()} record diproses…</p>
          )}
          {upload.error_message && (
            <p className="mt-0.5 text-xs text-red-600 dark:text-red-400" title={upload.error_message}>
              {upload.error_message.length > 80 ? upload.error_message.slice(0, 80) + "…" : upload.error_message}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={upload.status} />
          {isInFlight && (
            <Button variant="secondary" size="sm" onClick={onCancel}>
              <Ban className="mr-1 h-3 w-3" />
              Batalkan
            </Button>
          )}
        </div>
      </div>
      {(showProgress || upload.status === "pending") && (
        <div className="mt-2">
          <ProgressBar indeterminate />
        </div>
      )}
    </div>
  )
}

function UploadTableRow({
  upload,
  selected,
  onToggleSelect,
  onDelete,
}: {
  upload: UploadRow
  selected: boolean
  onToggleSelect: () => void
  onDelete: () => void
}) {
  const canDelete = !upload.status.match(/^(pending|processing)$/)

  return (
    <tr className={selected ? "bg-accent/5" : ""}>
      <td className="px-3 py-3">
        {canDelete && (
          <Checkbox
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Pilih ${upload.filename}`}
          />
        )}
      </td>
      <td className="max-w-[200px] truncate px-4 py-3 font-medium" title={upload.filename}>
        <Link href={`/admin/niq-fragrances/${upload.id}`} className="text-ink-display hover:text-accent hover:underline">
          {upload.filename}
        </Link>
      </td>
      <td className="px-4 py-3 capitalize text-ink-body">{upload.gender}</td>
      <td className="px-4 py-3 capitalize text-ink-body">{upload.dimension_type}</td>
      <td className="px-4 py-3 text-ink-body">{upload.dimension_value}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">
        {upload.row_count != null ? upload.row_count.toLocaleString() : "—"}
      </td>
      <td className="px-4 py-3 text-ink-body">{upload.uploaded_by ?? "—"}</td>
      <td className="px-4 py-3"><StatusBadge status={upload.status} /></td>
      <td className="px-4 py-3 text-xs text-ink-muted">
        {upload.imported_at ? formatDate(upload.imported_at) : formatDate(upload.created_at)}
      </td>
      <td className="px-4 py-3 text-right">
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1 text-ink-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
            title="Hapus upload ini"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

function UploadCard({
  upload,
  selected,
  onToggleSelect,
  onDelete,
}: {
  upload: UploadRow
  selected: boolean
  onToggleSelect: () => void
  onDelete: () => void
}) {
  const canDelete = !upload.status.match(/^(pending|processing)$/)

  return (
    <DataCard className={selected ? "border-accent/30 bg-accent/5" : ""}>
      <DataCardHeader>
        <div className="flex items-center gap-2">
          {canDelete && (
            <Checkbox
              checked={selected}
              onChange={onToggleSelect}
              aria-label={`Pilih ${upload.filename}`}
            />
          )}
          <DataCardTitle>
            <Link href={`/admin/niq-fragrances/${upload.id}`} className="break-all hover:text-accent hover:underline" title={upload.filename}>
              {upload.filename}
            </Link>
          </DataCardTitle>
        </div>
        <DataCardStatus><StatusBadge status={upload.status} /></DataCardStatus>
      </DataCardHeader>
      <DataCardGrid>
        <DataCardField label="Gender" value={<span className="capitalize">{upload.gender}</span>} />
        <DataCardField label="Tipe" value={<span className="capitalize">{upload.dimension_type}</span>} />
        <DataCardField label="Nilai Dimensi" value={upload.dimension_value} />
        <DataCardField
          label="Baris"
          value={upload.row_count != null ? upload.row_count.toLocaleString() : "—"}
        />
        <DataCardField label="Diunggah Oleh" value={upload.uploaded_by ?? "—"} />
        <DataCardField
          wide
          label="Diunggah"
          value={upload.imported_at ? formatDate(upload.imported_at) : formatDate(upload.created_at)}
        />
      </DataCardGrid>
      {canDelete && (
        <DataCardActions>
          <Button variant="ghost" size="sm" onClick={onDelete} className="gap-2 text-red-600">
            <Trash2 className="h-4 w-4" />
            Hapus
          </Button>
        </DataCardActions>
      )}
    </DataCard>
  )
}

function StatusBadge({ status }: { status: UploadStatus }) {
  switch (status) {
    case "pending":
      return <Badge tone="muted"><Clock className="mr-1 h-3 w-3" />Pending</Badge>
    case "processing":
      return <Badge tone="accent"><Loader2 className="mr-1 h-3 w-3 animate-spin" />Processing</Badge>
    case "completed":
      return <Badge tone="success"><CheckCircle2 className="mr-1 h-3 w-3" />Completed</Badge>
    case "failed":
      return <Badge tone="danger"><XCircle className="mr-1 h-3 w-3" />Failed</Badge>
    case "cancelled":
      return <Badge tone="muted"><Ban className="mr-1 h-3 w-3" />Dibatalkan</Badge>
  }
}

function ProgressBar({ value, indeterminate }: { value?: number; indeterminate?: boolean }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-hairline">
      {indeterminate ? (
        <div className="h-full w-1/3 animate-[progress-slide_1.4s_ease-in-out_infinite] rounded-full bg-accent" />
      ) : (
        <div
          className="h-full rounded-full bg-accent transition-all duration-300"
          style={{ width: `${Math.min(value ?? 0, 100)}%` }}
        />
      )}
    </div>
  )
}

function formatPeriod([year, month]: [number, number]): string {
  return `${MONTHS_SHORT[month]} ${String(year).slice(2)}`
}

function getCsrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })
}
